import { Graphics } from "pixi.js";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity, GameEventMap } from "../../../core/entity/Entity";
import { GameSprite } from "../../../core/entity/GameSprite";
import { on } from "../../../core/entity/handler";
import {
  memberFor,
  placementInFrame,
} from "../../../game/bench-work/bench-group";
import {
  benchPlacementFor,
  benchPointOnPallet,
  palletBerthPlacement,
  palletPointOnBench,
} from "../../../game/bench-work/bench-layout";
import {
  faceNails,
  isSameNail,
  PALLET_HEIGHT_IN,
  PALLET_WIDTH_IN,
  palletNailPosition,
} from "../../../game/bench-work/pallet-geometry";
import { Pallet, PalletNail } from "../../../game/Materials";
import {
  arrangeBenchMaterial,
  pryPalletNail,
} from "../../../sim/commands/bench-commands";
import {
  CLAW_LEVER,
  HAMMER_AT_REST,
  HAMMER_TUNING,
  hammerCursor,
  HammerCursor,
  hammerDown,
  hammerHold,
  hammerMove,
  HammerOutcome,
  HammerState,
  hammerUp,
} from "../../../game/bench-work/hammer-gesture";
import { playSound } from "../../../utils/sfx";
import { RewardFlights, supplyChip } from "../../RewardFlights";
import { ShellStore } from "../../ShellStore";
import { BenchDive } from "./BenchDive";
import { trayUnderPointer } from "./BenchTrayView";
import { BenchPiecesView } from "./BenchPiecesView";
import { benchStage, stagePointer, stageSettled } from "./benchStage";
import { nailPullFrame, StageFit } from "./stageMath";

/**
 * Pry mode — the first of the bench's gestures (migration phase 7). A
 * staged pallet offers its nails to the hammer with no plan selected
 * (docs/bench-work.md decision 0): every nail on the shown face wears a
 * "pry here" ring while the hammer is in hand, the one under the
 * pointer warms and widens, and the claw pulls it — the same hammer
 * press the bench's assembly reads (`bench-work/hammer-gesture.ts`):
 * hold on the nail until the hammer turns over, then drag along the
 * lever, and the nail rises with the hand and pops free once it clears.
 * The pop is the commit — one `pryPalletNail`, which drops the nail in
 * the shop's tin and frees any board that just lost its last one
 * (decision 4: work that moves resources commits incrementally). A
 * swing at a pallet nail only knocks: the face of the hammer drives,
 * the claw pulls.
 *
 * The picture follows the hand: the nail rises out of the hole along the
 * claw's lever as the pull comes, and once it is clear of the wood it
 * flies to the supplies tally as a reward chip (`RewardFlights`,
 * launched from the nail's own spot on screen). A board the pull freed
 * is announced to the pieces view as arriving from its berth
 * (`expectArrival`), so it slides onto the pile instead of appearing
 * there.
 */

/** How far a click may miss a nail and still mean it, in pallet inches. */
const NAIL_HIT_RADIUS_IN = 3.5;

/** How long the claw's lever line lingers over the hole after the pop. */
const PRY_ANIMATION_SECONDS = 0.12;

/** The direction the claw levers the nail out, in stage pixels — the
 * lever line's own heading. */
const LEVER_DX = 16;
const LEVER_DY = -22;
const LEVER_LENGTH = Math.hypot(LEVER_DX, LEVER_DY);

/** A pallet nail's length, and how far past the hole it ends up. */
const NAIL_LENGTH_IN = 2.5;
const NAIL_LENGTH_MAX_PX = 44;
const NAIL_CLEARANCE_PX = 8;

const RING = 0xf5efe3;
const RING_WARM = 0xd97c26;
const CLAW = 0x8a8378;
const NAIL_STEEL = 0xc9c3b8;
const NAIL_HEAD = 0x8f8a80;

export class BenchPryView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private rings: Graphics & GameSprite;
  /** The hammer's press, read by the pure gesture module. */
  private hammer: HammerState = HAMMER_AT_REST;
  /** The nail under the press, and where its hole sits on the stage. */
  private pressed: { nail: PalletNail; x: number; y: number } | null = null;
  /** Every swing so far, and when the last one came down — the cursor
   * restarts its swing on each. */
  private swings = 0;
  private swungAt = -Infinity;

  constructor() {
    super();
    // Placed in the dive's own frame ("parent" — this view is the dive
    // view's child), so the lean-in carries every surface on the stage
    // as one picture.
    this.rings = new Graphics() as Graphics & GameSprite;
    this.rings.layerName = "parent";
    this.sprite = this.rings;
  }

  private dive(): BenchDive | undefined {
    return this.game.entities.tryGetSingleton(BenchDive);
  }

  /**
   * Where the shown face's nails sit on screen right now — the pry
   * side of `piecePoints`: a spec aims at a real ring instead of
   * sweeping the stage for one.
   */
  nailPoints(): ReadonlyArray<{ nail: PalletNail; x: number; y: number }> {
    const staged = this.stagedPallet();
    const fit = benchStage(this.game)?.fit;
    if (!staged || !fit) return [];
    return faceNails(staged.pallet, staged.placement.flipped).map((nail) => ({
      nail,
      ...nailOnStage(staged.placement, nail, fit),
    }));
  }

  /**
   * The staged pallet's top-left corner, in the run's frame — where a
   * spec starts measuring to aim at a board rather than a nail.
   */
  palletCorner(): { xIn: number; yIn: number } | null {
    const staged = this.stagedPallet();
    if (!staged) return null;
    return {
      xIn: staged.placement.xIn - PALLET_WIDTH_IN / 2,
      yIn: staged.placement.yIn - PALLET_HEIGHT_IN / 2,
    };
  }

  /** The pallet staged on the opened bench, with where it lies. */
  private stagedPallet(): {
    pallet: Pallet;
    placement: ReturnType<typeof benchPlacementFor>;
  } | null {
    const dive = this.dive();
    const bench = dive?.openBench();
    if (!bench) return null;
    const pallet = bench.state.inputMaterials.find(
      (material) => material.type === "pallet",
    );
    if (!pallet || pallet.type !== "pallet") return null;
    return {
      pallet,
      placement: benchPlacementFor(bench.view(), pallet),
    };
  }

  /** The nail nearest the pointer, in bench-frame inches. Nearest wins:
   * neighboring crossings can sit closer together than the hit radius,
   * and the pointer means the closest one. */
  private nailAt(xIn: number, yIn: number): PalletNail | null {
    const staged = this.stagedPallet();
    const dive = this.dive();
    if (!staged || !dive) return null;
    const local = benchPointOnPallet(staged.placement, xIn, yIn);
    let best: PalletNail | null = null;
    let bestDist = NAIL_HIT_RADIUS_IN;
    for (const nail of faceNails(staged.pallet, staged.placement.flipped)) {
      const at = palletNailPosition(nail);
      const dist = Math.hypot(at.xIn - local.xIn, at.yIn - local.yIn);
      if (dist <= bestDist) {
        best = nail;
        bestDist = dist;
      }
    }
    return best;
  }

  /** The pointer in bench-frame inches, or null off the stage. */
  private pointerInches(): { xIn: number; yIn: number } | null {
    const stage = benchStage(this.game);
    return stage ? stagePointer(this.game, stage.fit) : null;
  }

  /** Whether the hammer is the tool in hand. */
  private hammerHeld(): boolean {
    return this.dive()?.heldTool === "hammer";
  }

  /** What the hand's hammer is doing, for the cursor — null unless the
   * hammer is in hand over a staged pallet. */
  hammerCursor(): {
    cursor: HammerCursor;
    swings: number;
    swungAt: number;
  } | null {
    if (!this.hammerHeld() || !this.stagedPallet()) return null;
    return {
      cursor: hammerCursor(this.hammer, performance.now(), HAMMER_TUNING),
      swings: this.swings,
      swungAt: this.swungAt,
    };
  }

  /** How far the pull in progress has come, 0..1, or null. */
  private pullProgress(): number | null {
    return this.hammer.press?.claw?.progress ?? null;
  }

  @on("tick")
  onTick() {
    const dive = this.dive();
    if (!dive || !dive.workable()) return;
    if (!this.hammerHeld() || !this.stagedPallet()) {
      if (dive.hoveredNail !== null) dive.hoveredNail = null;
      this.hammer = { ...HAMMER_AT_REST, swungMs: this.hammer.swungMs };
      this.pressed = null;
      return;
    }
    if (!this.hammer.press || !this.pressed) {
      const at = this.pointerInches();
      dive.hoveredNail = at ? this.nailAt(at.xIn, at.yIn) : null;
      return;
    }
    // The button is down: time passes on the hold, the hand may move
    const fit = benchStage(this.game)?.fit;
    if (!fit) return;
    const [px, py] = this.game.io.mousePosition;
    const now = performance.now();
    const overTarget =
      Math.hypot(px - this.pressed.x, py - this.pressed.y) <=
      NAIL_HIT_RADIUS_IN * fit.pxPerIn;
    const held = hammerHold(
      this.hammer,
      now,
      px,
      py,
      overTarget,
      HAMMER_TUNING,
    );
    this.hammer = held.state;
    if (held.outcome) this.resolve(held.outcome);
    const moved = hammerMove(this.hammer, px, py, CLAW_LEVER, HAMMER_TUNING);
    this.hammer = moved.state;
    if (moved.outcome) this.resolve(moved.outcome);
  }

  /** The press begins over a nail: the hammer cocks. */
  @on("mouseDown")
  onMouseDown() {
    const dive = this.dive();
    const bench = dive?.openBench();
    const stage = benchStage(this.game);
    if (!dive || !bench || !stage || !dive.workable()) return;
    if (!this.hammerHeld()) return;
    // Over a tray the press is the tray's (setting the hammer down)
    if (trayUnderPointer(this.game) !== null) return;
    const at = this.pointerInches();
    const nail = at ? this.nailAt(at.xIn, at.yIn) : null;
    const staged = this.stagedPallet();
    if (!nail || !staged) return;
    // Using the hammer hangs it: one taken up out of a tray goes on the
    // rail with the first press on a nail.
    if (!dive.settleHeldTool()) return;
    const [px, py] = this.game.io.mousePosition;
    this.pressed = { nail, ...nailOnStage(staged.placement, nail, stage.fit) };
    this.hammer = hammerDown(this.hammer, "nail", px, py, performance.now());
    dive.hoveredNail = null;
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("mouseUp")
  onMouseUp() {
    if (!this.hammer.press) return;
    const { state, outcome } = hammerUp(
      this.hammer,
      performance.now(),
      HAMMER_TUNING,
    );
    this.hammer = state;
    if (outcome) this.resolve(outcome);
    this.pressed = null;
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  /**
   * One outcome of the press. Only a completed pull does anything to the
   * pallet: the pop is the commit — one command frees the board AND
   * seats it. A swing at a pallet nail just knocks on it.
   */
  private resolve(outcome: HammerOutcome): void {
    const dive = this.dive();
    const bench = dive?.openBench();
    const stage = benchStage(this.game);
    const staged = this.stagedPallet();
    const pressed = this.pressed;
    if (!dive || !bench || !stage || !staged || !pressed) return;
    switch (outcome.kind) {
      case "blow":
      case "glance":
      case "miss":
        this.swings += 1;
        this.swungAt = performance.now();
        playSound("hammer-miss", 0.4);
        return;
      case "clawSet":
        playSound("claw-set", 0.4);
        return;
      case "pulled":
        break;
      default:
        return;
    }
    const nail = pressed.nail;
    // What the bench held before the pull is what tells a freed board
    // apart afterwards.
    const before = new Set(bench.state.inputMaterials.map((m) => m.id));
    if (!pryPalletNail(this.game, bench, nail)) return;
    dive.prying = { nail, secondsLeft: PRY_ANIMATION_SECONDS };
    dive.hoveredNail = null;

    // A freed board comes up off its berth and slides onto the pile.
    const pieces = this.game.entities.tryGetSingleton(BenchPiecesView);
    const member = memberFor(stage.group, bench.view());
    if (pieces && member) {
      for (const material of bench.state.inputMaterials) {
        if (before.has(material.id)) continue;
        const berth = palletBerthPlacement(
          staged.pallet.id,
          staged.placement,
          material.id,
        );
        if (!berth) continue;
        pieces.expectArrival(
          material.id,
          placementInFrame(stage.group, member, berth),
          { lifted: true },
        );
      }
    }

    // The nail flies to the tally from where the pull leaves it — clear
    // of the hole, at the end of the lever.
    this.launchNailFlight(staged.placement, nail, stage.fit);
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  private launchNailFlight(
    placement: ReturnType<typeof benchPlacementFor>,
    nail: PalletNail,
    fit: StageFit,
  ): void {
    const flights = this.game.entities.tryGetSingleton(RewardFlights);
    const canvas = this.game.renderer?.canvas;
    if (!flights || !canvas) return;
    const hole = nailOnStage(placement, nail, fit);
    const clear = nailPullFrame(1, nailLengthPx(fit), NAIL_CLEARANCE_PX).to;
    // The stage draws in the canvas's own pixels (the settled frame is
    // the identity), so a stage point is a window point once the
    // canvas's box is added.
    const rect = canvas.getBoundingClientRect();
    const flightId = `pry-${nail.deck}-${nail.stringer}-${performance.now()}`;
    flights.launch({
      id: flightId,
      originX: rect.left + hole.x + (LEVER_DX / LEVER_LENGTH) * clear,
      originY: rect.top + hole.y + (LEVER_DY / LEVER_LENGTH) * clear,
      chips: [supplyChip(flightId, "nails", "nail", 0)],
      supply: { id: "nails", amount: 1 },
    });
  }

  /**
   * F with the hammer in hand turns the pallet over from anywhere on the
   * stage — the rest of the nails are on the other side, and putting the
   * hammer down to turn it would be a step for nothing. Bare-handed F
   * belongs to the arranging view, which flips exactly the piece under
   * the pointer — the pallet included, now that it hit-tests like any
   * other piece — so one press only ever turns one thing.
   */
  @on("keyDown")
  onKeyDown({ key, event }: GameEventMap["keyDown"]) {
    if (key !== "KeyF") return;
    if (!this.hammerHeld()) return;
    if (!stageSettled(this.game)) return;
    const bench = this.dive()?.openBench();
    const staged = this.stagedPallet();
    if (!bench || !staged) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    arrangeBenchMaterial(this.game, bench, staged.pallet.id, {
      ...staged.placement,
      flipped: !staged.placement.flipped,
    });
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("render")
  onRender() {
    const g = this.rings;
    g.clear();
    const dive = this.dive();
    const fit = benchStage(this.game)?.fit;
    const staged = this.stagedPallet();
    if (!dive || !fit || !staged) return;

    if (this.hammerHeld()) {
      for (const nail of faceNails(staged.pallet, staged.placement.flipped)) {
        const { x, y } = nailOnStage(staged.placement, nail, fit);
        if (isSameNail(dive.hoveredNail, nail)) {
          // The hammer is over this one: the ring warms and widens.
          g.circle(x, y, 9).fill({ color: RING_WARM, alpha: 0.2 });
          g.circle(x, y, 9).stroke({ width: 3, color: RING_WARM, alpha: 1 });
        } else {
          g.circle(x, y, 7).stroke({ width: 2.5, color: RING, alpha: 0.95 });
        }
      }
    }

    // The pull in progress: the claw's lever over the hole, the nail
    // rising out of it along the lever with the hand, point last. After
    // the pop the nail is gone from the pallet and the lever lingers a
    // beat over the empty hole.
    const progress = this.pullProgress();
    const pulled = this.pressed && progress !== null ? this.pressed.nail : null;
    const lever = pulled ?? dive.prying?.nail ?? null;
    if (lever) {
      const { x, y } = nailOnStage(staged.placement, lever, fit);
      g.circle(x, y, 10).stroke({ width: 2.5, color: RING_WARM, alpha: 0.95 });
      g.moveTo(x, y)
        .lineTo(x + LEVER_DX, y + LEVER_DY)
        .stroke({ width: 3.5, color: CLAW, alpha: 0.9 });
      if (pulled && progress !== null) {
        const pull = nailPullFrame(
          progress,
          nailLengthPx(fit),
          NAIL_CLEARANCE_PX,
        );
        if (pull.to > pull.from) {
          const ux = LEVER_DX / LEVER_LENGTH;
          const uy = LEVER_DY / LEVER_LENGTH;
          g.moveTo(x + ux * pull.from, y + uy * pull.from)
            .lineTo(x + ux * pull.to, y + uy * pull.to)
            .stroke({ width: 2.5, color: NAIL_STEEL, alpha: 1 });
          g.circle(x + ux * pull.to, y + uy * pull.to, 3.5).fill({
            color: NAIL_HEAD,
            alpha: 1,
          });
        }
      }
    }
  }
}

/** A nail's hole on the stage, in pixels. */
function nailOnStage(
  placement: ReturnType<typeof benchPlacementFor>,
  nail: PalletNail,
  fit: StageFit,
): { x: number; y: number } {
  const local = palletNailPosition(nail);
  const at = palletPointOnBench(placement, local.xIn, local.yIn);
  return {
    x: fit.originX + at.xIn * fit.pxPerIn,
    y: fit.originY + at.yIn * fit.pxPerIn,
  };
}

/** The drawn nail's length at this zoom — a real nail's, up to a cap
 * that keeps it a nail and not a spike at the closest lean-in. */
function nailLengthPx(fit: StageFit): number {
  return Math.min(NAIL_LENGTH_IN * fit.pxPerIn, NAIL_LENGTH_MAX_PX);
}
